import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Input } from "../components/ui/input";
import Button from "../components/ui/button";
import { RadioGroup, RadioGroupItem } from "../../v2/components/ui/radio-group";
import { Label } from "../../v2/components/ui/label";
import { currencyList } from "../components/CurrencyDropdown";
import { CircleCheck, UploadIcon } from "lucide-react";
import { DocumentUpload } from "iconsax-reactjs";
import { toast } from "sonner";
import { handleUploadFileUtils } from "./approveRequest";
import axios from "axios";
import { cn } from "../libs/utils";

const units = {
  durationUnit: "working days",
  interestUnit: "% daily",
};

const disbursementType = {
  CreditWallet: 'Credit Wallet',
  ExternalAccount: 'External Account'
}

const DEFAULT_FORM_DATA = {
  requestedAmount: "",
  currency: "USDC",
  tenure: "",
  interestRate: "",
  lsaFile: null,
  managementFee: 0,
  fileName: "",
  disburseMethod: "",
  paymentSource: "",
  managementFeeDebitWalletType: ""
};

const validateFormData = (data) => {
  return {
    isValid:
      Boolean(data.requestedAmount) &&
      Boolean(data.tenure) &&
      Boolean(data.interestRate) &&
      Boolean(data.lsaFile),
    errors: {
      requestedAmount: !isNaN(data.requestedAmount) || !data.requestedAmount,
      tenure: !data.tenure,
      interestRate: !data.interestRate,
      lsaFile: !data.lsaFile,
    },
  };
};

const ApproveFinance = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [isUploading, setIsUploading] = useState(false);

  const { isValid: isFormValid } = validateFormData(formData);



  const isExternalDisabled = location?.state?.disburseMethod === 'CreditWallet';
  const showManagementFee = !!location?.state?.managementFee;

  useEffect(() => {
    if (location.state) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        ...Object.keys(DEFAULT_FORM_DATA).reduce((acc, key) => {
          if (key !== "disburseMethod" && key in location.state && location.state[key] != null) {
            acc[key] = location.state[key];
          }
          return acc;
        }, {}),
      }));
      if (location.state?.paymentFinance) {
        setFormData((prev) => ({
          ...prev,
          duration: location.state?.paymentFinance?.duration,
        }))
      }

    }
  }, [location.state]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        fileName: file.name,
      }));
    }
    if (file) {
      const fileTypes = ["JPEG", "JPG", "PNG", "PDF"];
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];

      if (!allowedTypes.includes(file?.type)) {
        toast.error("Please upload a JPEG, JPG, PNG, or PDF file");
        return;
      }
    }
    if (file?.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    setIsUploading(true);

    try {
      const uploadResponse = await handleUploadFileUtils({
        selectedFile: file,
        name: file.name,
      });
      setFormData((prev) => ({ ...prev, lsaFile: uploadResponse.link }));
      toast.success("File uploaded successfully");
    } catch (error) {
      toast.error("File upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleManagementFeeChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      managementFee: value,
    }));
  };

  const handlePayoutDestinationChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      disburseMethod: value,
    }));
  };

  const handlePaymentSourceChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      paymentSource: value,
    }));
  };
  const handlemManagementFeeDebitWalletType = (value) => {
    setFormData((prev) => ({
      ...prev,
      managementFeeDebitWalletType: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      const { requestedAmount, currency, tenure, interestRate, lsaFile, managementFee, disburseMethod, paymentSource, managementFeeDebitWalletType } = formData;
      const amountValue = Number(requestedAmount);
      const interestRateValue = Number(interestRate) / 100;
      const managementFeeValue = Number(managementFee);

      if (
        isNaN(amountValue) ||
        isNaN(interestRateValue) ||
        isNaN(tenure) ||
        isNaN(managementFeeValue)
      ) {
        toast.error("Please ensure all fields are filled out correctly.");
        setIsSubmitting(false);
        return;
      }

      const payload = {
        amount: amountValue,
        currency,
        tenure,
        interestRate: interestRateValue,
        debitManagementFee: Boolean(managementFeeValue),
      };

      if (disburseMethod) payload.disburseMethod = disburseMethod;
      if (paymentSource) payload.paymentSource = paymentSource;
      if (managementFeeDebitWalletType) payload.managementFeeDebitWalletType = managementFeeDebitWalletType;

      if (formData.lsaFile) {
        payload.signedAgreement = formData.lsaFile;
      }

      const response = await axios.post(`/v1/admin/new-finance/approve/${id}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.data?.error) {
        const result = await response?.data;

        await toast.success(result.message || "Request approved successfully");
        navigate(-1);
      } else {
        const error = await response?.data;
        toast.error(error.message || "Failed to approve request. Please try again.");
      }
    } catch (error) {
      console.log({ error });
      toast.error(error.message || "Failed to approve request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div className="">
      <nav
        className="w-11/12"
        style={{
          borderBottomWidth: "1px",
          borderStyle: "solid",
          borderImageSlice: 1,
          borderImageSource:
            "radial-gradient(circle at 50% 49.94%, #DCE0F9 0%, rgba(231,233,251,0) 100%)",
        }}
      >
        <div className="flex items-center gap-2">
          <button className="flex items-center justify-center hover:bg-gray-100">
            <span
              className="material-icons"
              style={{ color: "#344054" }}
              onClick={() => navigate(-1)}
            >
              close
            </span>
          </button>
          <h1 className="text-xl font-medium text-gray-900">Approve request</h1>
        </div>
      </nav>

      <main className="w-11/12 mx-auto md:w-1/2 md:max-w-[592px] mt-12 mb-3">
        <div>
          <h1 className="text-xl font-medium text-gray-900">Approve loan request</h1>

          <p className="text-[#667085] text-base font-normal">
            Update details of the agreed upon terms
          </p>
        </div>
        <form onSubmit={handleSubmit} className="mt-8 space-y-8">
          {/* Offered amount */}
          <div className="space-y-2">
            <h3 className="text-sm font-[450] text-[#4F5372]">Offered amount</h3>
            <div className="flex items-end gap-2">
              <div className="flex items-center gap-2 !bg-[#ECEFF3] p-2.5 rounded-[8px] h-[56px] w-[96px]">
                {currencyList
                  .filter((currency) => currency.text === formData.currency)
                  .map((currency) => (
                    <img
                      key={currency.text}
                      src={currency.img}
                      alt={`${currency.text} flag`}
                      className="size-7"
                    />
                  ))}

                <span className="text-base font-normal text-[#667085]">{formData.currency}</span>
              </div>
              <div className="flex-1">
                <Input
                  type="text"
                  name="requestedAmount"
                  value={formData.requestedAmount}
                  onChange={handleInputChange}
                  placeholder="65,000"
                  newInput={true}
                />
              </div>
            </div>
          </div>

          {/* Duration */}
          <div className="space-y-2">
            <h3 className="text-sm font-[450] text-[#4F5372]">Duration</h3>
            <div className="flex items-center justify-between gap-4 !bg-[#ECEFF3] w-full !rounded-[8px] h-[56px] shadow-none !border-0 px-3 py-2 text-base">
              <input
                type="number"
                name="tenure"
                value={formData.tenure}
                onChange={handleInputChange}
                placeholder="3"
                max={3}
                className="text-base focus:*:ring-0 w-auto max-w-auto focus:outline-none border-0 bg-transparent"
              />

              <span className="text-sm font-normal text-[#4F5372]">{units.durationUnit}</span>
            </div>
          </div>

          {/* Interest rate */}
          <div className="space-y-2">
            <h3 className="text-sm font-[450] text-[#4F5372]">Interest rate</h3>
            <div className="flex items-center justify-between gap-4 !bg-[#ECEFF3] w-full !rounded-[8px] h-[56px] shadow-none !border-0 px-3 py-2 text-base">
              <input
                type="number"
                step="0.01"
                name="interestRate"
                value={formData.interestRate}
                onChange={handleInputChange}
                placeholder="2.00"
                className="text-base focus:*:ring-0 w-[80px] max-w-auto focus:outline-none border-0 bg-transparent"
              />
              <span className="text-sm font-normal text-[#4F5372]">{units.interestUnit}</span>
            </div>
          </div>

          {/* Signed loan service agreement */}
          <div className="space-y-2">
            <h3 className="text-sm font-[450] text-[#4F5372]">Signed loan service agreement</h3>
            <div className="flex items-center relative cursor-pointer justify-between gap-4 !bg-[#ECEFF3] w-full !rounded-[8px] h-[56px] shadow-none !border-0 px-3 py-2 text-base">
              <input
                type="file"
                onChange={handleFileChange}
                format="image/*,.pdf"
                className="absolute w-full h-full opacity-0 cursor-pointer"
              />
              <span className="flex items-center">
                <DocumentUpload className="mr-2 size-6" />
                {isUploading
                  ? "Uploading..."
                  : formData.fileName
                    ? formData.fileName
                    : "Upload signed LSA"}
              </span>
            </div>

            {!isUploading && formData.lsaFile && (
              <p className="flex items-center gap-1 text-sm text-success-10">
                {" "}
                <CircleCheck /> File uploaded successfully
              </p>
            )}
          </div>

          {/* Payout destination */}
          <div className="space-y-3">
            <h3 className="text-[15px] font-semibold text-gray-900">Payout destination</h3>
            <RadioGroup
              value={formData.disburseMethod}
              onValueChange={handlePayoutDestinationChange}
              className="flex items-center gap-6"
            >
              <div className="flex items-center space-x-2.5">
                <RadioGroupItem value="CreditWallet" id="CreditWallet" className="w-4 h-4 !accent-primary-30 border-gray-300 text-violet-700 focus:ring-violet-700" />
                <p htmlFor="CreditWallet" className="text-[15px] font-medium cursor-pointer text-[#344054]">
                  Credit wallet
                </p>
              </div>
              <div className={cn("flex items-center space-x-2.5", isExternalDisabled && 'opacity-50')}>
                <RadioGroupItem disabled={isExternalDisabled} value="ExternalAccount" id="ExternalAccount" className="w-4 h-4 !accent-primary-30 border-gray-300 text-violet-700 focus:ring-violet-700" />
                <p htmlFor="ExternalAccount" className={cn("text-[15px] font-medium cursor-pointer text-[#344054]")}>
                  External wallet
                </p>
              </div>
            </RadioGroup>
            {location?.state?.disburseMethod && (
              <p className="text-[15px] italic text-[#667085] mt-2">
                Preferred destination: <span className="font-semibold text-gray-900">{disbursementType[location?.state?.disburseMethod]}</span>
              </p>
            )}
          </div>

          {/* Management fee */}
          {showManagementFee && <div className="space-y-3">
            <h3 className="text-[15px] font-semibold text-gray-900">Management fee</h3>
            <RadioGroup
              value={formData.managementFee}
              onValueChange={handleManagementFeeChange}
              className="flex items-center gap-6"
            >
              <div className="flex items-center space-x-2.5">
                <RadioGroupItem value={1} id="1" className="w-4 h-4 !accent-primary-30 border-gray-300 text-violet-700 focus:ring-violet-700" />
                <p htmlFor="1" className="text-[15px] font-medium cursor-pointer text-[#344054]">
                  Yes
                </p>
              </div>
              <div className="flex items-center space-x-2.5">
                <RadioGroupItem value={0} id="0" className="w-4 h-4 !accent-primary-30 border-gray-300 text-violet-700 focus:ring-violet-700" />
                <p htmlFor="0" className="text-[15px] font-medium cursor-pointer text-[#344054]">
                  No charge
                </p>
              </div>
            </RadioGroup>
          </div>}

          {/* Payment source */}
          {showManagementFee && <div className="space-y-3">
            <h3 className="text-[15px] font-semibold text-gray-900">Payment source</h3>
            <RadioGroup
              value={formData.managementFeeDebitWalletType}
              onValueChange={handlemManagementFeeDebitWalletType}
              className="flex items-center gap-6"
            >
              <div className="flex items-center space-x-2.5">
                <RadioGroupItem value="FINANCE" id="FINANCE" className="w-4 h-4 !accent-primary-30 border-gray-300 text-violet-700 focus:ring-violet-700" />
                <p htmlFor="FINANCE" className="text-[15px] font-medium cursor-pointer text-[#344054]">
                  Deduct from USDC wallet
                </p>
              </div>
              <div className="flex items-center space-x-2.5">
                <RadioGroupItem value="TRADEVU" id="TRADEVU" className="w-4 h-4 !accent-primary-30 border-gray-300 text-violet-700 focus:ring-violet-700" />
                <p htmlFor="TRADEVU" className="text-[15px] font-medium cursor-pointer text-[#344054]">
                  Deduct from loan amount
                </p>
              </div>
            </RadioGroup>
          </div>}

          {/* Submit button */}
          <Button
            type="submit"
            disabled={isSubmitting || !isFormValid}
            className="w-[246px] h-[56px] mt-8 text-sm font-semibold text-white rounded-[8px] bg-violet-700 hover:bg-violet-600 disabled:bg-violet-700/70 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Processing..." : "Approve request"}
          </Button>
        </form>
      </main>
    </div>
  );
};

export default ApproveFinance;
